import { useState, useEffect } from 'react';
import { Play, Heart } from 'lucide-react';
import { ContentRail } from './ContentRail';
import { formatDuration } from '../../utils/homeHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BecauseYouFollowSection({ navigate, onPlayVideo }) {
  const [items, setItems] = useState([]);
  const [teamNames, setTeamNames] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchData = async () => {
      try {
        const [followRes, contentRes] = await Promise.all([
          fetch(`${API}/user/following`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }),
          fetch(`${API}/content/all`),
        ]);
        const followData = await followRes.json();
        const contentData = await contentRes.json();

        const teams = (followData.teams || []).map(t => t.toLowerCase());
        if (!teams.length) return;

        setTeamNames(followData.teams || []);

        const matched = contentData
          .filter(c => {
            const str = (c.teams || c.title || '').toLowerCase();
            return teams.some(t => str.includes(t));
          })
          .slice(0, 20);

        setItems(matched);
      } catch {
        // silent
      }
    };
    fetchData();
  }, []);

  if (!items.length) return null;

  const primaryTeam = teamNames[0] || '';
  const subtitle = teamNames.length === 1
    ? `Because you follow ${primaryTeam}`
    : teamNames.length > 1
    ? `Because you follow ${teamNames.slice(0, 2).join(' & ')}${teamNames.length > 2 ? ` +${teamNames.length - 2} more` : ''}`
    : 'Content from teams you follow';

  return (
    <ContentRail
      icon={<Heart className="w-5 h-5 text-red-400 shrink-0" fill="currentColor" />}
      title="Because You Follow"
      subtitle={subtitle}
      seeAllPath="/following"
      navigate={navigate}
      testId="because-you-follow-section"
    >
      {items.map((content) => (
        <div
          key={content.id}
          onClick={() => {
            if (content.source === 'youtube' && content.youtube_video_id) onPlayVideo(content);
            else navigate(`/match/${content.id}`);
          }}
          className="flex-shrink-0 w-[260px] sm:w-[300px] cursor-pointer group snap-start"
          data-testid={`follow-card-${content.id}`}
        >
          <div className="relative aspect-video rounded-lg overflow-hidden mb-2">
            <img src={content.thumbnail_url} alt={content.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            {content.is_live ? (
              <div className="absolute top-2 left-2 flex items-center gap-1">
                <span className="px-1.5 py-0.5 bg-red-600 text-white text-[9px] font-bold rounded animate-pulse">LIVE</span>
                <span className="px-1.5 py-0.5 bg-emerald-500/80 text-white text-[8px] font-bold rounded" data-testid={`your-team-live-${content.id}`}>Your team is playing</span>
              </div>
            ) : null}
            {!content.is_live && content.competition_name && (
              <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full">
                <span className="text-emerald-400 text-[8px] font-bold uppercase tracking-wider">{content.competition_name}</span>
              </div>
            )}
            {content.duration && (
              <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-[9px] font-medium rounded">{formatDuration(content.duration)}</div>
            )}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="w-11 h-11 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30">
                <Play className="w-4 h-4 text-white" fill="currentColor" />
              </div>
            </div>
          </div>
          <h3 className="text-white font-semibold text-sm group-hover:text-red-400 transition-colors line-clamp-1">{content.teams || content.title}</h3>
          <p className="text-gray-500 text-[11px] mt-0.5">
            {content.is_live ? 'Live now' : content.duration ? formatDuration(content.duration) : ''}
            {content.competition_name ? ` \u00B7 ${content.competition_name}` : ''}
          </p>
        </div>
      ))}
    </ContentRail>
  );
}
